import { useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Suggestion } from "./types";

const SUGGESTION_ITEM_HEIGHT = 36;
const SUGGESTION_OVERSCAN = 5;
const VISIBLE_SUGGESTION_ROWS = 7;

type TokenSuggestionPopupProps = {
    insertCallback: (suggestion: Suggestion) => void;
    selectedIndex: number;
    suggestions: Suggestion[];
    title: string;
};

export const TokenSuggestionPopup = ({
    insertCallback,
    selectedIndex,
    suggestions,
    title,
}: TokenSuggestionPopupProps) => {
    const scrollElementRef = useRef<HTMLDivElement | null>(null);
    const viewportHeight = useMemo(
        () => Math.min(suggestions.length, VISIBLE_SUGGESTION_ROWS) * SUGGESTION_ITEM_HEIGHT,
        [suggestions.length],
    );
    const virtualizer = useVirtualizer({
        count: suggestions.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => SUGGESTION_ITEM_HEIGHT,
        overscan: SUGGESTION_OVERSCAN,
    });

    useEffect(() => {
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            virtualizer.scrollToIndex(selectedIndex, { align: "auto" });
        }
    }, [selectedIndex, suggestions.length, virtualizer]);

    return (
        <div className="token-suggestion-container">
            <div className="token-suggestion-title">{title}</div>
            <div
                ref={scrollElementRef}
                aria-label={title}
                className="token-suggestion-viewport"
                role="listbox"
                style={{ height: viewportHeight }}
            >
                <div
                    className="token-suggestion-list"
                    style={{
                        height: `${virtualizer.getTotalSize()}px`,
                    }}
                >
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                        const suggestion = suggestions[virtualItem.index];

                        return (
                            <div
                                aria-selected={selectedIndex === virtualItem.index}
                                key={`${suggestion.text}-${virtualItem.index}`}
                                className={`token-suggestion-item ${selectedIndex === virtualItem.index ? "active" : ""}`}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    insertCallback(suggestion);
                                }}
                                role="option"
                                style={{
                                    height: `${virtualItem.size}px`,
                                    transform: `translateY(${virtualItem.start}px)`,
                                }}
                                tabIndex={selectedIndex === virtualItem.index ? 0 : -1}
                            >
                                <span className="token-suggestion-name">{suggestion.displayText}</span>
                                {suggestion.description && (
                                    <span className="token-suggestion-description" title={suggestion.description}>
                                        {suggestion.description}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
